import { isUndefined } from 'lodash';
import moment, { Moment } from 'moment';
import {
  BALANCE_SHEET_PROP,
  CASH_AND_EQUIVALENTS_ALIAS,
  FOURTH_ROW_ROW_NUMBER,
  INCOME_STATEMENT_PROP,
} from 'common/constants/financials';
import { FinancialsPerformanceMetricsColumn, FinancialStatementPeriod } from 'common/types/financials';
import { IMeasurementDate } from 'models/interfaces/measurement-date.interface';
import { MOMENT_SUPPORTED_DATE_FORMATS, parseValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { getColumnValue } from './auxiliaryFunctions';
import { getRowSpan } from './getCellSpan';
import getClassName from './getClassName';
import getPrevYearColumn from './getPrevYearColumn';
import { BalanceSheetRowConfig } from '../balance-sheet/types';
import { ntmAndLtmHeadersStyle } from '../data/baseRowConfig';

const getPairedCell = (
  cell: FinancialsPerformanceMetricsColumn, // cell is a LTM or NTM cell
  sameRowCells: FinancialsPerformanceMetricsColumn[],
  currentFiscalYearEnd: Moment,
  measurementDate: IMeasurementDate,
  reported_statement_date: string | null
) => {
  if (cell.date) {
    const cellDate = reported_statement_date ?? cell.date;
    const measurementDateObject = moment(measurementDate.date, MOMENT_SUPPORTED_DATE_FORMATS);
    const measurementDateControl = cell.isLTM ? measurementDateObject : measurementDateObject.clone().add(1, 'year');
    const currentFiscalYearEndControl = cell.isLTM ? currentFiscalYearEnd : currentFiscalYearEnd.clone().add(1, 'year');
    const measurementDateControlIso = measurementDateControl.toISOString();
    const currentFiscalYearEndControlIso = currentFiscalYearEndControl.toISOString();
    const cellDateIso = moment(cellDate).toISOString();
    if (measurementDateControlIso !== currentFiscalYearEndControlIso || measurementDateControlIso !== cellDateIso) {
      return null;
    }
    // we've checked out the other conditions so we just need to find the cell with the same date (if any)
    return sameRowCells.find(c => moment(c.date).toISOString() === moment(cellDate).toISOString());
  }
  return null;
};

const baseParser = async ({
  columns = [],
  rowConfig,
  currencyCode,
  tableData: { currentFiscalYearEnd, measurementDate },
}: {
  columns: FinancialStatementPeriod[];
  rowConfig: BalanceSheetRowConfig[];
  currencyCode: string;
  tableData: { currentFiscalYearEnd: Moment; measurementDate: IMeasurementDate };
}) => {
  let cells: Record<string, FinancialsPerformanceMetricsColumn> = {};
  /* baseParser is also used in Income statement. With this variable we can
  modify just the parent columns expressions in Balance Sheet */
  const balanceSheetOrigin = rowConfig[3].alias === CASH_AND_EQUIVALENTS_ALIAS;
  const financialsInformationKey = balanceSheetOrigin ? BALANCE_SHEET_PROP : INCOME_STATEMENT_PROP;

  const alphabet = alphabetGenerator([], columns.length);

  const getExpr = (expr: string | null | undefined, columnLegend: string) =>
    expr ? expr.replace(/@/g, columnLegend) : '';
  const getValue = (v: string) => (!isUndefined(v) ? v : null);

  columns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];
    const { isLTM, isNTM } = column;
    const columnTableData = column[financialsInformationKey];

    rowConfig.forEach((row, index) => {
      const rowNumber = index + 1;
      const key = columnLegend + rowNumber;
      const type = row.gridType || null;

      const columnValue = getColumnValue({
        rowAlias: row.alias,
        column,
        columnTableData,
        isColumnWithReportedDate: Boolean(column.isLTMYear || column.isNTMYear),
      });
      const value = getValue(columnValue as string);
      const expr = getExpr(row.validExpr ?? (row.expr as string), columnLegend);
      const parsedValue = parseValue(value, type, null, null, row.dbType);

      const date = columnTableData?.reported_statement_date ?? column.statement_date;

      const { needs_actualization } = column;
      const className = getClassName(Boolean(needs_actualization), row);

      const isAdditionalHistoricalYear = Boolean(column.isAdditionalHistoricalYear);
      const showAdditionalHistoricalYear = Boolean(column.showAdditionalHistoricalYear);
      const isVisibleColumn = isAdditionalHistoricalYear
        ? isAdditionalHistoricalYear && showAdditionalHistoricalYear
        : null;

      const currentCell = {
        ...row,
        date,
        key,
        columnLegend,
        columnIndex,
        needs_actualization,
        className,
        columnId: column.id,
        rowNumber,
        isLTM,
        isNTM,
        expr,
        value: parsedValue,
        alias: row.alias || '',
        isParent: Boolean(column.isParent),
        rowSpan: getRowSpan(rowNumber, column),
        prevYear: getPrevYearColumn({ columnIndex, column, alphabet }),
        isProjectionPeriod: column.isProjectedYear,
        isParentWithDivider: Boolean(column.isParentWithDivider),
        needsSpacer: Boolean(column.needsSpacer),
        isAdditionalHistoricalYear,
        showAdditionalHistoricalYear,
        isVisibleColumn,
        mdMatchesFiscalYearEnd: column.mdMatchesFiscalYearEnd,
        year: column.year,
        style: (isNTM || isLTM) && rowNumber < FOURTH_ROW_ROW_NUMBER ? ntmAndLtmHeadersStyle : row.style,
        currencyCode,
      } as FinancialsPerformanceMetricsColumn;
      const isSpecialPeriod = isLTM || isNTM;

      if (isSpecialPeriod && !currentCell.readOnly) {
        const sameRowCells = Object.values(cells).filter(
          c => c.key !== currentCell.key && currentCell.rowNumber === c.rowNumber
        );
        // find cell with same row and same date and add it as child of controller by expression
        const pairedCell = getPairedCell(
          currentCell,
          sameRowCells,
          currentFiscalYearEnd,
          measurementDate,
          columnTableData?.reported_statement_date ?? null
        );
        if (pairedCell) {
          currentCell.customKey = `controller_${currentCell.key}_${pairedCell.key}`;
          pairedCell.expr = `=${currentCell.key}`;
        }
      }

      cells = {
        ...cells,
        [key]: currentCell,
      };
    });
  });

  return cells;
};

export default baseParser;
